<script lang="ts">

    import VariationsComparison from './VariationsComparison.svelte';
    import AiHint from './AiHint.svelte';
    import ProductImage from './ProductImage.svelte';
    import { formatPriceUnitAppend, formatPriceUnitPrepend } from './formatPrice.js';
    import RatingStars from './RatingStars.svelte';
    import { trackClick } from './trackClick';
    import Skeleton from './Skeleton.svelte';
    import type { PricePresentation } from '../pricePresentation';

    interface Props {
      variationId: string
      variationsComparisonHeading: string
      variationsComparisonCaption: string
      imageUrl: string
      imageAltText: string
      brandName: string
      name: string
      retailPrice: number
      suggestedRetailPrice?: number
      pricePresentation: PricePresentation
      normPrice?: number
      normUnit?: string
      variationUrl: string
      averageRatingRounded?: number
      numberOfReviews?: number
      energyEfficiencyClass?: string
      energyEfficiencyColor?: string
      energyEfficiencyLabelVersion?: 'default' | 'uniform'
      animated?: boolean
    }

    let {
      variationId,
      variationsComparisonHeading,
      variationsComparisonCaption,
      imageUrl,
      imageAltText,
      brandName,
      name,
      retailPrice,
      suggestedRetailPrice,
      pricePresentation,
      normPrice,
      normUnit,
      variationUrl,
      averageRatingRounded,
      numberOfReviews,
      energyEfficiencyClass,
      energyEfficiencyColor,
      energyEfficiencyLabelVersion,
      animated = false,
    }: Props = $props();

    let normPriceFormatted = $derived.by<string | undefined>(() => {
      if (!normPrice || normPrice == 0 || !normUnit) {
        return undefined;
      }

      return `(${formatPriceUnitPrepend(normPrice)}/${normUnit})`;
    });

    let skeletonActive = $state<boolean>(false);

    const inactivateSkeleton = () => {
      skeletonActive = false;
    };

    let aiHintHidden = $state<boolean>(false);

    $effect(() => {
      skeletonActive = animated;
      aiHintHidden = animated;
    });

    const showAiHint = () => {
      aiHintHidden = false;
    };
</script>

<a
    class="ft5-upselling-link"
    data-transaction-name="FT5 Upselling Component"
    href={variationUrl}
    onclick={() => trackClick(variationId)}
>
    <div class="ft5-upselling-container">
        <VariationsComparison
            animated={animated}
            animationFinished={inactivateSkeleton}
            heading={variationsComparisonHeading}
            caption={variationsComparisonCaption}
            style="padding: 8px 8px 0px 8px;"
        />
        <div class="ft5-upselling-data-container">
            <div class="ft5-upselling-data">
                <ProductImage
                    altText={imageAltText}
                    url={imageUrl}
                />
                <div class="ft5-upselling-product-data">
                    <div class="ft5-upselling-product-data-head">
                        <div class="ft5-upselling-brand-name">
                            <div class="oc-headline-25">{brandName}</div>
                            <div class="ft5-upselling-name oc-copy-75">{name}</div>
                        </div>
                        {#if energyEfficiencyClass}
                            <oc-energy-label-v1
                                variant={energyEfficiencyClass}
                                color={energyEfficiencyColor}
                                version={energyEfficiencyLabelVersion}
                            ></oc-energy-label-v1>
                        {/if}
                    </div>
                    <div class="ft5-upselling-prices oc-copy-75">
                        <div class="ft5-upselling-retail-price oc-copy--bold"
                             class:ft5-upselling-retail-price--sale={pricePresentation.redRetailPrice}>
                            {formatPriceUnitAppend(retailPrice)}
                        </div>
                        {#if pricePresentation.showUvp && suggestedRetailPrice}
                            <div class="ft5-upselling-suggested-price">UVP
                                <span class:ft5-upselling-linethrough={pricePresentation.strikethrough}>
                                    {formatPriceUnitAppend(suggestedRetailPrice)}
                                </span>
                            </div>
                        {/if}
                    </div>
                    {#if (numberOfReviews ?? 0) > 0 || normPriceFormatted}
                        <div class="ft5-upselling-additional oc-copy-50">
                            {#if normPriceFormatted}
                                {normPriceFormatted}
                            {/if}
                            {#if (numberOfReviews ?? 0) > 0}
                                <RatingStars averageRatingRounded={averageRatingRounded ?? 0}/>
                                <span>({numberOfReviews})</span>
                            {/if}
                        </div>
                    {/if}
                </div>
            </div>
            <Skeleton active={skeletonActive} animationFinished={showAiHint}/>
        </div>
    </div>
</a>
<AiHint hidden={aiHintHidden}/>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";

  .ft5-upselling-link {
    text-decoration: none;
  }

  .ft5-upselling-container {
    padding: 8px;
    background-color: #f0f0f0;
    border-radius: 16px;
  }

  .ft5-upselling-data-container {
    position: relative;
  }

  .ft5-upselling-data {
    display: flex;
    gap: 12px;
    margin-top: 8px;
    padding: 8px;
    background-color: #ffffff;
    border-radius: 12px;
  }

  .ft5-upselling-product-data {
    min-width: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .ft5-upselling-product-data-head {
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ft5-upselling-brand-name {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .ft5-upselling-name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-top: 4px;
  }

  .ft5-upselling-prices {
    margin-top: 4px;
    display: flex;
    gap: 8px;
  }

  .ft5-upselling-retail-price--sale {
    color: tokens.$oc-semantic-color-text-sale
  }

  .ft5-upselling-suggested-price {
    color: tokens.$oc-semantic-color-text-secondary
  }

  .ft5-upselling-linethrough {
    text-decoration: line-through;
  }

  .ft5-upselling-additional {
    display: flex;
    align-items: center;
    margin-top: 4px;
    gap: 4px;
    color: tokens.$oc-semantic-color-text-secondary
  }
</style>
